import { inject, Injectable, signal } from '@angular/core';
import { DeviceStoreService } from '../services/device-store.service';
import { PlayerApiService } from './api/player-api.service';
import { catchError, map, Observable, of } from 'rxjs';
import { Uuid } from '@hidat/huijs-common';
import { PlayerInstance, PlayerListenerItem, PlayerMix, PlayerProfile } from '../models';
import { SerializationLevel } from '@hidat/huijs-api';
import { StationManagerService } from './station-manager.service';
import { PlayerInstanceApiPayload } from '@splixer/common';
import { TimelineSegmentItem } from '../services/timeline.service';

/**
 * Instance and Listener Service
 * Manages the current 'instance', which is a browser/device based unique identifier.
 * This also manages the current listener profile, since the listener will be loaded with the instance,
 * and many listeners functions can be applied to just the instance.
 */
@Injectable({ providedIn: 'root' })
export class InstanceManagerService {
  private deviceStore = inject(DeviceStoreService);
  private station = inject(StationManagerService);
  private api = inject(PlayerApiService);

  private readonly defaultInstanceIncludes = 'profile,tags,recents[host]';
  private _loaded = false;
  public instanceId: Uuid | undefined;
  public listenerId: Uuid | undefined;

  public listenerProfile = signal<PlayerProfile | undefined>(undefined);
  public listenerItems = signal<PlayerListenerItem[]>([]);
  //public trackTags = signal<PlayerTag[]>([]);
  public recentlyPlayed = signal<PlayerMix []>([]);

  public bookmarks: any[] = [];
  public recentBookmarks: any[] = [];

  public hasInstance = signal(false);
  public hasListener = signal(false);

  /**
   * Loads the given instance.  Returns true on success, false on failure.
   * @param stationId
   * @param instanceId
   * @param listenerId
   * @param linkCode
   */
  public loadInstance(
    stationId: Uuid,
    instanceId: Uuid | undefined | null,
    listenerId: Uuid | undefined | null,
    linkCode?: string,
  ): Observable<boolean> {
    let canLoad = false;
    if (listenerId || instanceId) {
      const payload: PlayerInstanceApiPayload = {
        stationId: stationId,
        includes: this.defaultInstanceIncludes,
      };
      if (instanceId) {
        payload.instanceId = instanceId;
        canLoad = true;
      }
      if (listenerId) {
        payload.listenerId = listenerId;
        canLoad = true;
      }
      if (linkCode) {
        payload.linkCode = linkCode;
        canLoad = true;
      }

      if (canLoad) {
        return this.api.instances
          .putOne([], payload, SerializationLevel.Both)
          .pipe(
            catchError((err) => {
              if (err && err.status === 404) {
                //DEV this.deviceStore.clear();
              }
              return of(undefined);
            }),
            map((results) => {
              if (results) {
                this.load(results as PlayerInstance);
                return true;
              } else {
                console.debug('Session not found, clearing it');
                //DEV this.deviceStore.clear();
              }
              return false;
            }),
          );
      }
    }
    return of(false);
  }

  /**
   * Loads the instance from the given api hash, and saves the instance id and listener id if it has changed.
   * @param instance
   */
  public load(instance: PlayerInstance): void {
    if (instance.uid) {
      console.debug('Loading Instance ', instance.uid)
      this.instanceId = instance.uid;
      this.hasInstance.set(true);

      this.listenerId = instance.listenerId;
      if (instance._profile) {
        this.listenerProfile.set(instance._profile);
        this.hasListener.set(true);
      }
      if (instance._listenerItems) {
        this.listenerItems.set(instance._listenerItems);
      }
      if (instance._recents) {
        this.recentlyPlayed.set(instance._recents);
      }
      if (instance.uid) {
        this.deviceStore.updateInstance(instance.uid);
      }
      if (
        this.listenerId &&
        this.listenerId !== this.deviceStore.listenerId()
      ) {
        this.deviceStore.listenerId(this.listenerId);
      }
      this._loaded = true;
    }
  }

  /**
   * Valid Instance?
   * Returns true if there is current an instance loaded
   */
  public loaded(): boolean {
    return this._loaded;
  }

  /**
   * Send feedback
   * @param message
   */

  /* GIW
  public sendFeedback(
    message: string
  ): Observable<AnyHash | undefined> | undefined {
    if (this.instanceId) {
      return this.api.instances.sendFeedback(
        this.instanceId,
        message,
      );
    }
    return undefined;
  }

  flagSet(set: TimelineSegmentItem, flagType?: string): Promise<any> {
    return this.api.instances.flagSegment(this.instance.instanceId, set.segment.id, null).then((results) => {
      this.toasterService.info("Thanks for flagging this set, we'll have someone check it out.", 'Set Flagged');
    });
  }
  }

  likeTrack(item: TimelineSegmentItem): void {
    console.log('Item Bookmarked: ', item.segmentIndex);
    if (this.instanceId && item.playbackLogId) {
      this.api.instances
        .tagResource(this.instanceId, 'bookmark_content', item.playbackLogId)
        .subscribe(
          (results) => {
            //this.notify.success('Track saved!');
          },
          (error) => {
            //this.notify.error('There was an error bookmarking the track!');
          },
        );
    }
  }*/

  public addListenerItem(
    item: PlayerListenerItem
  ): Observable<boolean> {
    if (this.station.id) {
      const payload: PlayerInstanceApiPayload = {
        stationId: this.station.id,
        includes: this.defaultInstanceIncludes,
        instanceId: this.instanceId,
        listenerId: this.listenerId,
        listenerItem: item,
      };
      return this.api.instances
        .postOne(['add_item'], payload, SerializationLevel.Output)
        .pipe(
          map((res) => {
            if (res?.uid) {
              this.load(res as PlayerInstance);
              return true;
            }
            return false;
          }),
        );
    }
    return of(false);
  }

  /* GIW
  public destroyBookmark(userTag, notifyResults = true): Promise<any> {
    console.debug('Destroying Bookmark: ', userTag.id);
    let p = this.api.destroyUserTag(this.instanceId, userTag.id);
    p.then((results) => {
        this.bookmarkedCount--;
        this.updateBookmarkCache(results);
        if (notifyResults) {
          this.notify.success('Track Deleted!')
        }
        return results;
      }
      ,
      (error) => {
        if (notifyResults)
          this.notify.systemError("There was an error while deleting the track.");
      }
    );
    return p;
  }


  public destroyLikedSet(userTag, notifyResults = true): Promise<any> {
    let p = this.api.destroyUserTag(this.instanceId, userTag.id);
    p.then((results) => {
        // Try and delete the mix from the user mixes
        const index = this.likedSets.findIndex((item) => {
          return item.id == results.id
        });
        if (index >= 0) {
          this.likedSets.splice(index, 1);
        }
        if (notifyResults) {
          this.notify.success('Set Deleted!')
        }
        return results;
      }
      ,
      (error) => {
        if (notifyResults)
          this.notify.systemError("There was an error while deleting the set.");
      }
    );
    return p;
  }
  */

  /**
   * Loads the instance AND the user information.
   * This includes any custom mixes, tags, etc.
   * If a user has yet to be defined, then only the custom data associated with the instance is returned.
   * @param {string} instanceId
   * @param {string} userId
   * @param {string} includes
   * @returns {Promise<boolean>}
   */
  /* GIW
  public getUserInfo(instanceId: string, includes?: string) {
    return this.api.loadUserInfo(instanceId, includes).then(results => {
      this.instanceId = results.id;
      this.userId = results.user_id;
      this.user = results._user;
      this.station = results._station;
      this.mixCount = results._mix_count;
      this.userMixes = results._user_mixes;
      this.likedSets = results._liked_sets;
      this.likedCount = results._liked_count;
      if (results._liked_duration > 0) {
        this.likedDuration = (results._liked_duration / 3600);
      }
      this.bookmarkedCount = results._bookmarked_count;
      this.bookmarks = results._bookmarks;
      this.updateBookmarkCache();
      return (results);
    })
  }
   */

  public emailLink(email: string): Observable<boolean> {
    if (this.station.id) {
      const payload: PlayerInstanceApiPayload = {
        stationId: this.station.id,
        includes: '',
        instanceId: this.instanceId,
        email: email,
      };
      return this.api.instances
        .postOne(['email_link'], payload, SerializationLevel.Output)
        .pipe(
          map((res) => {
            if (res?.uid) {
              this.load(res as PlayerInstance);
              return true;
            }
            return false;
          }),
        );
    }
    return of(false);
  }

  public verifyLink(code: string): Observable<boolean> {
    if (this.station.id) {
      const payload: PlayerInstanceApiPayload = {
        stationId: this.station.id,
        includes: this.defaultInstanceIncludes,
        instanceId: this.instanceId,
        linkCode: code,
      };
      return this.api.instances
        .postOne(['verify_link'], payload, SerializationLevel.Output)
        .pipe(
          map((res) => {
            if (res?.uid) {
              this.load(res as PlayerInstance);
              return true;
            }
            return false;
          }),
        );
    }
    return of(false);
  }
}
